<script setup lang="ts">
import type { Customer, Product } from '@/types'
import { Head, Link } from '@inertiajs/vue3'
import { dashboardLayout } from '@/lib/layouts'
import { adminCustomerPath, adminProductPath } from '@/routes'

defineOptions({
  layout: dashboardLayout,
})

defineProps<{
  product: Pick<Product, 'id' | 'slug' | 'name'>
  customers: Pick<Customer, 'id' | 'name' | 'active'>[]
}>()
</script>

<template>
  <Head :title="`${product.name} — instances`" />

  <UDashboardPanel id="product-instances">
    <template #header>
      <UDashboardNavbar title="Licenses & instances">
        <template #leading>
          <UButton
            :to="adminProductPath(product.slug)"
            :as="Link"
            icon="i-lucide-arrow-left"
            color="neutral"
            variant="ghost"
            square
          />
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div class="flex flex-col gap-3">
        <p class="text-muted text-sm">
          Customers licensed to use <span class="text-highlighted font-medium">{{ product.name }}</span>.
          Grant or revoke access from each customer's page.
        </p>

        <div
          v-if="customers.length === 0"
          class="ring-default text-muted rounded-lg p-6 text-center text-sm ring"
        >
          No customer instances for this product yet.
        </div>

        <ul v-else class="divide-default ring-default divide-y rounded-lg ring">
          <li
            v-for="customer in customers"
            :key="customer.id"
            class="flex items-center justify-between gap-4 px-4 py-3"
          >
            <Link
              :href="adminCustomerPath(customer.id)"
              class="text-highlighted truncate font-medium hover:underline"
            >
              {{ customer.name }}
            </Link>
            <UBadge
              :color="customer.active ? 'success' : 'neutral'"
              variant="subtle"
              size="sm"
            >
              {{ customer.active ? 'Active' : 'Inactive' }}
            </UBadge>
          </li>
        </ul>
      </div>
    </template>
  </UDashboardPanel>
</template>
